/**
 * Graph utility functions for workflow processing
 */

import type { WorkflowReactFlowNode, WorkflowReactFlowEdge } from '@/types/imageWorkflow';

/**
 * Topological sort to order nodes by their execution sequence.
 * Nodes with no incoming edges (sources) come first, then their dependents.
 * Uses Kahn's algorithm for stable ordering.
 */
export function topologicalSort(
  nodes: WorkflowReactFlowNode[],
  edges: WorkflowReactFlowEdge[]
): WorkflowReactFlowNode[] {
  // Build adjacency list and in-degree count
  const adjacency = new Map<string, string[]>();
  const inDegree = new Map<string, number>();

  // Initialize
  for (const node of nodes) {
    adjacency.set(node.id, []);
    inDegree.set(node.id, 0);
  }

  // Build graph
  for (const edge of edges) {
    const neighbors = adjacency.get(edge.source);
    if (neighbors) {
      neighbors.push(edge.target);
    }
    inDegree.set(edge.target, (inDegree.get(edge.target) ?? 0) + 1);
  }

  // Kahn's algorithm
  const queue: string[] = [];
  const result: WorkflowReactFlowNode[] = [];
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  // Start with nodes that have no incoming edges
  for (const node of nodes) {
    if ((inDegree.get(node.id) ?? 0) === 0) {
      queue.push(node.id);
    }
  }

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    const node = nodeMap.get(nodeId);
    if (node) {
      result.push(node);
    }

    // Decrease in-degree for neighbors
    for (const neighbor of adjacency.get(nodeId) ?? []) {
      const newInDegree = (inDegree.get(neighbor) ?? 1) - 1;
      inDegree.set(neighbor, newInDegree);
      if (newInDegree === 0) {
        queue.push(neighbor);
      }
    }
  }

  // If some nodes couldn't be sorted (cycle or disconnected), append them
  if (result.length < nodes.length) {
    const sortedIds = new Set(result.map((n) => n.id));
    for (const node of nodes) {
      if (!sortedIds.has(node.id)) {
        result.push(node);
      }
    }
  }

  return result;
}

/**
 * Get node key from a React Flow node
 * Handles both nodeKey in data and rf- prefixed IDs
 */
export function getNodeKey(node: WorkflowReactFlowNode): string {
  return node.data.nodeKey || node.id.replace(/^rf-/, '');
}
